// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { toODataString } from '@progress/kendo-data-query'

import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import { setCurrentBooking, setData, setLoading, setModalFacturacion } from './actions'
import {
  BOOKING_CAMBIAR_ESTADO_BOOKING,
  BOOKING_CARGAR_DATA,
  BOOKING_FACTURAR_BOOKING,
  GET_BOOKINGS_RESERVAS,
  LOADING_BOOKINGS_RESERVAS,
  SET_BOOKINGS_RESERVAS,
} from './actionsTypes'

import { StateCatalogos } from '../../catalogos/CatalogosReducer'

import { RootState } from '../../store'

import { apiActividadMaestra, apiBookings } from '@/common/constants/api'
import { Region } from '@/common/enums/region.enum'
import { get, post, put as putBooking } from '@/common/helpers/api_helper'
import { notifications } from '@/common/helpers/notifications'
import { getBookingReservas } from '@/common/helpers/odataApiServices'
import {
  eliminarFiltroEspecial,
  eliminarFiltroSalesProfit,
  filtroEspecial,
  filtroSalesProfit,
  fusionarFiltros,
  simplificarFechasOData,
} from '@/common/helpers/utils'
import { toDate } from '@/common/helpers/utilsDate'
import { IBooking, IBookingsState, ICambioEstadoBooking } from '@/common/interfaces/booking'

import { Estado } from '@/common/interfaces/estado'
import { getRuntimeConfig } from '@/core/config/runtimeConfig'

const Booking = (state: RootState): IBookingsState => state.Booking
const Catalogos = (state: RootState): StateCatalogos => state.Catalogos

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getBookings = (next: string): Promise<any> => {
  return get(`${apiBookings}&${next}`)
    .then((res) => {
      return {
        data: res.value.map((b: IBooking) => {
          b.FechaInicio = toDate(b.FechaInicio)
          b.FechaFin = toDate(b.FechaFin)
          b.ClienteNames = `${b.Cliente.Nombre}`

          return b
        }),
        total: res['@odata.count'],
      }
    })
    .catch((error) => {
      notifications({ ex: error })
    })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getActiviadMaestraByBookingCode = (BookingCode: string): Promise<any> => {
  return get(`${apiActividadMaestra}&&$filter=BookingCode eq '${BookingCode}'`)
    .then((res) => {
      return res.value[0].ActividadMaestra
    })
    .catch((error) => {
      notifications({ ex: error })
    })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* getBookingsByPage(next: string): any {
  yield put(setLoading(true))
  const bookings = yield call(getBookings, next)

  // Solo actualizar los datos si la respuesta es válida
  if (bookings && bookings.data) {
    yield put(setData(bookings))
  }
  yield put(setLoading(false))
}

// Se exporta para poder testear el corte temprano sin levantar el store completo
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* actualizarEstadoBooking(booking: IBooking, estadoNuevo: Estado): any {
  const estadoAntiguo = booking.EstadoId

  try {
    const actividadMaestra = yield call(getActiviadMaestraByBookingCode, booking.BookingCode)

    // getActiviadMaestraByBookingCode atrapa su propio error y devuelve undefined; también devuelve
    // null si el booking no tiene ActividadMaestra. Sin este corte la asignación de EstadoId lanzaba
    // un TypeError que, al estar esta tarea en un fork, subía hasta el rootSaga y mataba todas las sagas.
    if (!actividadMaestra) {
      notifications({
        title: 'Error',
        message: 'No se pudo obtener la actividad maestra del booking',
        toastType: 'error',
      })

      return
    }

    actividadMaestra.EstadoId = estadoNuevo.EstadoId
    booking.EstadoId = estadoNuevo.EstadoId
    booking.Estado = estadoNuevo

    yield call(
      putBooking,
      `Bookings(${booking.BookingId})`,
      estadoNuevo.Nombre !== 'Operación'
        ? booking
        : {
            ...booking,
            FechaEnvioFacturacion: null,
            CodigoNetSuitePeruOrdenFactura: null,
            SyncStatusNetSuitePeruOrdenFactura: null,
            UltimaFechaIntegracionNetSuitePeruOrdenFactura: null,
          },
    )
    yield call(putBooking, `ActividadesMaestras(${actividadMaestra.ActividadMaestraId})`, actividadMaestra)

    // Limpiar campos NetSuite OrdenFactura del state local al regresar a Operación
    if (estadoNuevo.Nombre === 'Operación') {
      booking.FechaEnvioFacturacion = null
      booking.CodigoNetSuitePeruOrdenFactura = null
      booking.SyncStatusNetSuitePeruOrdenFactura = null
      booking.UltimaFechaIntegracionNetSuitePeruOrdenFactura = null
    }

    const newCambioEstadoCerrar: ICambioEstadoBooking = {
      BookingId: booking.BookingId,
      EstadoAntiguoId: estadoAntiguo,
      EstadoNuevoId: estadoNuevo.EstadoId,
      FechaCambio: new Date(),
    }

    yield call(post, `CambiosEstadoBooking`, newCambioEstadoCerrar)
    yield put(setCurrentBooking(booking))
    notifications({
      title: 'Éxito',
      message: 'Booking actualizado',
      toastType: 'success',
    })
  } catch (error) {
    notifications({ ex: error })
  }
}

function* actualizarEstadoBookingFacturar(booking: IBooking, estadoFacturar: Estado): Generator {
  const bookingFacturar = { ...booking }

  try {
    yield put(setModalFacturacion(true))
    const response: {
      bookingId: number
      bookingCode: string
      estadoId: number
      fechaEnvioFacturacion: string
      codigoIntegracion: string | null
      syncStatusIntegracion: string | null
      fechaIntegracion: string | null
      tipoCodigoJdeOrdenVenta: string | null
    } = yield call(
      post,
      `GenerarOrdenesFacturacion(${bookingFacturar.BookingId},Factura,${getRuntimeConfig().REGION})`,
      '',
    )

    // Actualizar campos locales del booking con lo que devuelve el back
    if (response) {
      bookingFacturar.EstadoId = estadoFacturar.EstadoId
      bookingFacturar.Estado = estadoFacturar

      const fechaIntegracion = response.fechaIntegracion ? toDate(response.fechaIntegracion) : undefined

      switch (getRuntimeConfig().REGION) {
        // JDE
        case Region.Ecuador:
          bookingFacturar.CodigoJdeOrdenVenta = response.codigoIntegracion
          bookingFacturar.SyncStatusJdeOrdenVenta = response.syncStatusIntegracion
          bookingFacturar.UltimaFechaIntegracionJdeOrdenVenta = fechaIntegracion
          bookingFacturar.TipoCodigoJdeOrdenVenta = response.tipoCodigoJdeOrdenVenta
          break

        // NetSuite Peru
        case Region.Peru:
          bookingFacturar.CodigoNetSuitePeruOrdenFactura = response.codigoIntegracion
          bookingFacturar.SyncStatusNetSuitePeruOrdenFactura = response.syncStatusIntegracion
          bookingFacturar.UltimaFechaIntegracionNetSuitePeruOrdenFactura = fechaIntegracion
          break

        default:
          break
      }
    }

    yield put(setModalFacturacion(false))

    yield put(setCurrentBooking(bookingFacturar))
    notifications({
      title: 'Éxito',
      message: `El booking con código: ${bookingFacturar.BookingCode} se ha enviado a facturación`,
      toastType: 'success',
      seRegistra: true,
    })
  } catch (error) {
    // `?.` en toda la cadena: un error sin respuesta HTTP (timeout, red caída) no trae `response`,
    // y un TypeError aquí dentro del catch subía por el fork hasta el rootSaga y mataba todas las
    // sagas, con el modal de facturación girando para siempre.
    const errorIntegracion = error?.response?.data?.detail || error?.message || 'Error al generar órdenes de facturación'

    switch (getRuntimeConfig().REGION) {
      // JDE
      case Region.Ecuador:
        bookingFacturar.SyncStatusJdeOrdenVenta = errorIntegracion
        break

      // NetSuite Peru
      case Region.Peru:
        bookingFacturar.SyncStatusNetSuitePeruOrdenFactura = errorIntegracion
        break

      default:
        break
    }

    yield put(setCurrentBooking(bookingFacturar))
    yield put(setModalFacturacion(false))
    notifications({ ex: error })
  }
}

function* getBookingsData(): Generator {
  const { dataState }: IBookingsState = yield select(Booking)
  const filtroEOperativo = filtroEspecial(dataState) // buscar EOperativo y construir filtro
  const filtroSP = filtroSalesProfit(dataState)
  const dataStateSinEspeciales = eliminarFiltroSalesProfit(eliminarFiltroEspecial(dataState))
  const dataStringSinEspeciales = toODataString(dataStateSinEspeciales)
  const dataFusionEOperativo = fusionarFiltros(dataStringSinEspeciales, filtroEOperativo)
  const dataFusion = fusionarFiltros(dataFusionEOperativo, filtroSP)
  const dataFusionSimplificado = simplificarFechasOData(dataFusion) // simplifica fechas ISO a YYYY-MM-DD

  yield fork(getBookingsByPage, dataFusionSimplificado)
}

function* cambioEstadoBooking(action: { type: string; payload: number }): Generator {
  const { selectedBooking }: IBookingsState = yield select(Booking)

  if (!selectedBooking) {
    return
  }

  const { estados }: StateCatalogos = yield select(Catalogos)
  const estadosCopy = structuredClone(estados)
  const selectedBookingCopy = structuredClone(selectedBooking)
  const nuevoEstado = estadosCopy['Booking'][action.payload]

  yield fork(actualizarEstadoBooking, selectedBookingCopy, nuevoEstado)
}

function* facturarBooking(): Generator {
  const { selectedBooking }: IBookingsState = yield select(Booking)

  if (!selectedBooking) {
    return
  }

  const { estados }: StateCatalogos = yield select(Catalogos)
  const estadoFacturar = estados['Booking']['Enviado a facturar']

  yield fork(actualizarEstadoBookingFacturar, selectedBooking, estadoFacturar)
}

function* getBookingsReservas(): void {
  yield put({ type: LOADING_BOOKINGS_RESERVAS, payload: true })
  const bookingsR = yield call(getBookingReservas)
  const bookingReservas = bookingsR?.value ?? []

  yield put({ type: SET_BOOKINGS_RESERVAS, payload: bookingReservas })
  yield put({ type: LOADING_BOOKINGS_RESERVAS, payload: false })
}

function* watchCargarBookings(): Generator {
  // takeLatest y no takeEvery: cada cambio de filtro dispara una consulta, y con takeEvery las
  // concurrentes no se cancelaban — si la del filtro viejo respondía después, pisaba los
  // resultados del nuevo (el grid mostraba datos que no correspondían a lo filtrado). La
  // cancelación alcanza al fork de getBookingsByPage porque es un fork adjunto de esta tarea.
  yield takeLatest(BOOKING_CARGAR_DATA, getBookingsData)
}

function* watchCambioEstadoBooking(): Generator {
  yield takeEvery(BOOKING_CAMBIAR_ESTADO_BOOKING, cambioEstadoBooking)
}

function* watchFacturarBooking(): Generator {
  yield takeEvery(BOOKING_FACTURAR_BOOKING, facturarBooking)
}

function* watchGetBookingsReservas(): Generator {
  yield takeEvery(GET_BOOKINGS_RESERVAS, getBookingsReservas)
}

function* bookingSaga(): Generator {
  yield all([
    fork(watchCargarBookings),
    fork(watchCambioEstadoBooking),
    fork(watchFacturarBooking),
    fork(watchGetBookingsReservas),
  ])
}

export default bookingSaga
